import { useEffect } from "react";
import { FieldErrors } from "react-hook-form";

import { PaymentMethod } from "@/Apollo/schema";
import { Device } from "@/common/components/AppDownloadButton";
import {
  AnalyticsLogInStatus,
  AnalyticsMe,
  AnalyticsOrderGroup,
  AnalyticsProduct,
  AnalyticsProvider,
  AnalyticsQuickBidMethod,
  AnalyticsService,
  AnalyticsViewItemOptions,
  CheckoutSummaryEditTarget,
  HpClickTarget,
} from "@/common/contracts/analytics.contracts";
import { ChatMemberRole } from "@/common/contracts/chat.contracts";
import { algoliaAnalyticsProvider } from "@/common/services/analytics/providers/algolia-provider";
import { googleAnalyticsProvider } from "@/common/services/analytics/providers/google-analytics.provider";
import { iterableAnalyticsProvider } from "@/common/services/analytics/providers/iterable-provider";
import { pinterestAnalyticsProvider } from "@/common/services/analytics/providers/pinterest.provider";
import { statsigAnalyticsProvider } from "@/common/services/analytics/providers/statsig.provider";
import { BulkUploadEntryPoint } from "@/common/static/bulk-upload.static";
import { CheckoutStep } from "@/common/static/checkout/checkout.static";
import { isNil } from "@/common/utils/value.utils";

/**
 * Private registry of all registered service providers.
 */
const PROVIDERS: AnalyticsProvider[] = [
  googleAnalyticsProvider,
  algoliaAnalyticsProvider,
  iterableAnalyticsProvider,
  statsigAnalyticsProvider,
  pinterestAnalyticsProvider,
];

/**
 * Helper function that calls specific func on all registered providers.
 *
 * @param func Function identifier.
 * @param args Function arguments.
 */
const callProviderFunc = <TFunc extends keyof AnalyticsProvider>(
  func: TFunc,
  ...args: Parameters<Required<AnalyticsProvider>[TFunc]>
) => {
  for (const provider of PROVIDERS) {
    const providerFunc = provider[func];

    if (providerFunc) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (providerFunc as (...args: any[]) => void)(...args);
    }
  }
};

/**
 * Main analytics service responsible for triggering the same event over all registered providers.
 */
export const analyticsService: AnalyticsService = {
  useExecAfterMount: (func: () => void) => {
    useEffect(() => {
      func();
    }, [func]);
  },
  trackAddToWishlist: (product?: AnalyticsProduct) => {
    if (isNil(product)) {
      return;
    }

    callProviderFunc("trackAddToWishlist", product);
  },
  trackSearch: (
    query?: string,
    resultsCount?: number,
    searchImage?: string,
    defaultSearch?: boolean,
    experimentVariant?: string
  ) => {
    if (isNil(resultsCount) || (isNil(query) && isNil(searchImage))) {
      return;
    }

    callProviderFunc(
      "trackSearch",
      query,
      resultsCount,
      searchImage,
      defaultSearch,
      experimentVariant
    );
  },
  trackFormError: (
    formName?: string,
    fieldName?: string,
    errorText?: string
  ) => {
    if (!formName || !fieldName || !errorText) {
      return;
    }

    callProviderFunc("trackFormError", formName, fieldName, errorText);
  },
  trackAllFormErrors: (formName: string, errors: FieldErrors) => {
    for (const key in errors) {
      const keyMessage = errors[key]?.message;
      const message =
        keyMessage && typeof keyMessage === "string" ? keyMessage : "Required";

      /** Trigger `form_error` event. */
      analyticsService.trackFormError(formName, key, message);
    }
  },
  trackClickedWhoppahCourierCheckout: (product?: AnalyticsProduct) => {
    if (isNil(product)) {
      return;
    }

    callProviderFunc("trackClickedWhoppahCourierCheckout", product);
  },
  trackNavigationMenu: (label?: string) => {
    if (isNil(label)) {
      return;
    }

    callProviderFunc("trackNavigationMenu", label);
  },
  trackProBuyerHeaderClick: (surface, state) => {
    if (isNil(surface) || isNil(state)) {
      return;
    }

    callProviderFunc("trackProBuyerHeaderClick", surface, state);
  },
  trackFooterNavigation: (label?: string) => {
    if (isNil(label)) {
      return;
    }

    callProviderFunc("trackFooterNavigation", label);
  },
  trackSignupLoginSticky: () => {
    callProviderFunc("trackSignupLoginSticky");
  },
  trackLogin: (me?: AnalyticsMe) => {
    if (isNil(me)) {
      return;
    }

    callProviderFunc("trackLogin", me);
  },
  trackLogout: () => {
    callProviderFunc("trackLogout");
  },
  trackViewItemList: (
    listId?: string,
    listName?: string,
    products?: AnalyticsProduct[]
  ) => {
    if (
      isNil(listId) ||
      isNil(listName) ||
      isNil(products) ||
      products.length === 0
    ) {
      return;
    }

    callProviderFunc("trackViewItemList", listId, listName, products);
  },
  trackSelectItem: (
    position?: number,
    listId?: string,
    listName?: string,
    product?: AnalyticsProduct,
    searchMeta?: {
      queryId?: string;
      indexName?: string;
      page?: number;
      limit?: number;
      fromSearch?: boolean;
    },
    searchImage?: string
  ) => {
    if (
      isNil(position) ||
      isNil(listId) ||
      isNil(listName) ||
      isNil(product) ||
      isNil(searchMeta)
    ) {
      return;
    }

    callProviderFunc(
      "trackSelectItem",
      position,
      listId,
      listName,
      product,
      searchMeta,
      searchImage
    );
  },
  trackFilterClickList: (filterType?: string, filterOptions?: unknown) => {
    /* An empty array is a terms filter switched off completely: send nothing, like the apps. */
    if (
      isNil(filterType) ||
      isNil(filterOptions) ||
      (Array.isArray(filterOptions) && filterOptions.length === 0)
    ) {
      return;
    }

    callProviderFunc("trackFilterClickList", filterType, filterOptions);
  },
  trackFilterExpandList: (filterType?: string) => {
    if (isNil(filterType)) {
      return;
    }

    callProviderFunc("trackFilterExpandList", filterType);
  },
  trackFilterResetList: () => {
    callProviderFunc("trackFilterResetList");
  },
  trackSaveAlertList: () => {
    callProviderFunc("trackSaveAlertList");
  },
  trackSoldAlternativesShown: (
    level?: string,
    priceBand?: string,
    matchCount?: number,
    shown?: number
  ) => {
    if (isNil(level) || isNil(priceBand) || isNil(matchCount) || isNil(shown)) {
      return;
    }

    callProviderFunc(
      "trackSoldAlternativesShown",
      level,
      priceBand,
      matchCount,
      shown
    );
  },
  trackSaveAlertSoldPdp: () => {
    callProviderFunc("trackSaveAlertSoldPdp");
  },
  trackSaveAlertSearch: () => {
    callProviderFunc("trackSaveAlertSearch");
  },
  trackSortItemsList: (sortType?: string) => {
    if (isNil(sortType)) {
      return;
    }

    callProviderFunc("trackSortItemsList", sortType);
  },
  trackPaginationList: (pageNumber?: number) => {
    if (isNil(pageNumber)) {
      return;
    }

    callProviderFunc("trackPaginationList", pageNumber);
  },
  trackNavigationChipsList: (chipCategory?: string) => {
    if (isNil(chipCategory)) {
      return;
    }

    callProviderFunc("trackNavigationChipsList", chipCategory);
  },
  trackViewItem: (
    product?: AnalyticsProduct,
    options?: AnalyticsViewItemOptions
  ) => {
    if (isNil(product)) {
      return;
    }

    callProviderFunc("trackViewItem", product, options);
  },
  trackAddToCart: (product?: AnalyticsProduct) => {
    if (isNil(product)) {
      return;
    }

    callProviderFunc("trackAddToCart", product);
  },
  trackRemoveFromCart: (product?: AnalyticsProduct) => {
    if (isNil(product)) {
      return;
    }

    callProviderFunc("trackRemoveFromCart", product);
  },
  trackShare: () => {
    callProviderFunc("trackShare");
  },
  trackPlaceBidPdp: () => {
    callProviderFunc("trackPlaceBidPdp");
  },
  trackReadMoreBidPdp: () => {
    callProviderFunc("trackReadMoreBidPdp");
  },
  trackPlaceBidSuccessPdp: (
    bidMethod?: AnalyticsQuickBidMethod,
    product?: AnalyticsProduct
  ) => {
    if (isNil(bidMethod) || isNil(product)) {
      return;
    }

    callProviderFunc("trackPlaceBidSuccessPdp", bidMethod, product);
  },
  trackImageZoomPdp: (imageNumber?: number) => {
    if (isNil(imageNumber)) {
      return;
    }

    callProviderFunc("trackImageZoomPdp", imageNumber);
  },
  trackImageBrowsePdp: () => {
    callProviderFunc("trackImageBrowsePdp");
  },
  trackLaunchedArPdp: () => {
    callProviderFunc("trackLaunchedArPdp");
  },
  trackContactSellerPdp: () => {
    callProviderFunc("trackContactSellerPdp");
  },
  trackContactSellerMessageSentPdp: () => {
    callProviderFunc("trackContactSellerMessageSentPdp");
  },
  trackReadMoreBuyerProtectionPdp: () => {
    callProviderFunc("trackReadMoreBuyerProtectionPdp");
  },
  trackReadMoreDeliveryOptionsPdp: () => {
    callProviderFunc("trackReadMoreDeliveryOptionsPdp");
  },
  trackReadMoreParcelPdp: () => {
    callProviderFunc("trackReadMoreParcelPdp");
  },
  trackReadMorePickupPdp: () => {
    callProviderFunc("trackReadMorePickupPdp");
  },
  trackReadMoreCourierPdp: () => {
    callProviderFunc("trackReadMoreCourierPdp");
  },
  trackReadMoreCuratedPdp: () => {
    callProviderFunc("trackReadMoreCuratedPdp");
  },
  trackReadMoreBrengerPdp: () => {
    callProviderFunc("trackReadMoreBrengerPdp");
  },
  trackReadMoreReviewsPdp: () => {
    callProviderFunc("trackReadMoreReviewsPdp");
  },
  trackSimilarItemPdp: () => {
    callProviderFunc("trackSimilarItemPdp");
  },
  trackShowSimilarItemPdp: () => {
    callProviderFunc("trackShowSimilarItemPdp");
  },
  trackShowMoreSimilarItemPdp: () => {
    callProviderFunc("trackShowMoreSimilarItemPdp");
  },
  trackSeeMoreClickPdp: () => {
    callProviderFunc("trackSeeMoreClickPdp");
  },
  trackClickedUspCard: () => {
    callProviderFunc("trackClickedUspCard");
  },
  trackClickedBrandPromiseBlock: () => {
    callProviderFunc("trackClickedBrandPromiseBlock");
  },
  trackViewCart: (products?: AnalyticsProduct[]) => {
    if (isNil(products) || products.length === 0) {
      return;
    }

    callProviderFunc("trackViewCart", products);
  },
  trackViewCartOverview: (products?: AnalyticsProduct[]) => {
    if (isNil(products) || products.length === 0) {
      return;
    }

    callProviderFunc("trackViewCartOverview", products);
  },
  trackEmptyCartOverview: () => {
    callProviderFunc("trackEmptyCartOverview");
  },
  trackBeingCheckout: (products?: AnalyticsProduct[]) => {
    if (isNil(products) || products.length === 0) {
      return;
    }

    callProviderFunc("trackBeingCheckout", products);
  },
  trackAddShippingInfo: () => {
    callProviderFunc("trackAddShippingInfo");
  },
  trackAddPaymentInfo: (
    products?: AnalyticsProduct[],
    paymentType?: PaymentMethod
  ) => {
    if (isNil(products) || products.length === 0 || isNil(paymentType)) {
      return;
    }

    callProviderFunc("trackAddPaymentInfo", products, paymentType);
  },
  trackClickedStepperBackCheckout: (step?: CheckoutStep) => {
    if (isNil(step)) {
      return;
    }

    callProviderFunc("trackClickedStepperBackCheckout", step);
  },
  trackClickedSummaryEditCheckout: (target?: CheckoutSummaryEditTarget) => {
    if (isNil(target)) {
      return;
    }

    callProviderFunc("trackClickedSummaryEditCheckout", target);
  },
  trackClickedCouponCheckout: () => {
    callProviderFunc("trackClickedCouponCheckout");
  },
  trackClickedFeeTooltipCheckout: () => {
    callProviderFunc("trackClickedFeeTooltipCheckout");
  },
  trackClickedPickupCheckout: (product?: AnalyticsProduct) => {
    if (isNil(product)) {
      return;
    }

    callProviderFunc("trackClickedPickupCheckout", product);
  },
  trackClickedExternalBrengerCheckout: (product?: AnalyticsProduct) => {
    if (isNil(product)) {
      return;
    }

    callProviderFunc("trackClickedExternalBrengerCheckout", product);
  },
  trackClickedPostalCheckout: (product?: AnalyticsProduct) => {
    if (isNil(product)) {
      return;
    }

    callProviderFunc("trackClickedPostalCheckout", product);
  },
  trackClickedServiceCheckout: (
    product?: AnalyticsProduct,
    serviceName?: string
  ) => {
    if (isNil(product) || isNil(serviceName)) {
      return;
    }

    callProviderFunc("trackClickedServiceCheckout", product, serviceName);
  },
  trackPurchase: (orderGroup?: AnalyticsOrderGroup) => {
    if (isNil(orderGroup)) {
      return;
    }

    callProviderFunc("trackPurchase", orderGroup);
  },
  trackHpSellNow: () => {
    callProviderFunc("trackHpSellNow");
  },
  trackHpLearnHowToSell: () => {
    callProviderFunc("trackHpLearnHowToSell");
  },
  trackHpCategoryNavigation: (navigationItem?: string) => {
    if (isNil(navigationItem)) {
      return;
    }

    callProviderFunc("trackHpCategoryNavigation", navigationItem);
  },
  trackHpBrandNavigation: (navigationItem?: string) => {
    if (isNil(navigationItem)) {
      return;
    }

    callProviderFunc("trackHpBrandNavigation", navigationItem);
  },
  trackHpShopBrands: () => {
    callProviderFunc("trackHpShopBrands");
  },
  trackHpCollectionNavigation: (navigationItem?: string) => {
    if (isNil(navigationItem)) {
      return;
    }

    callProviderFunc("trackHpCollectionNavigation", navigationItem);
  },
  trackHpNewsletterSubscription: () => {
    callProviderFunc("trackHpNewsletterSubscription");
  },
  trackHpReadAllAbout: (articleName?: string) => {
    if (isNil(articleName)) {
      return;
    }

    callProviderFunc("trackHpReadAllAbout", articleName);
  },
  trackHpSocialMediaLinks: (platform?: string) => {
    if (isNil(platform)) {
      return;
    }

    callProviderFunc("trackHpSocialMediaLinks", platform);
  },
  trackHpMarketingBanner: () => {
    callProviderFunc("trackHpMarketingBanner");
  },
  trackHpNewIn: (clickTarget?: HpClickTarget) => {
    if (isNil(clickTarget)) {
      return;
    }

    callProviderFunc("trackHpNewIn", clickTarget);
  },
  trackHpForYou: (clickTarget?: HpClickTarget) => {
    if (isNil(clickTarget)) {
      return;
    }

    callProviderFunc("trackHpForYou", clickTarget);
  },
  trackHpFavorites: () => {
    callProviderFunc("trackHpFavorites");
  },
  trackPromotion: (
    event?:
      | "offers_viewed"
      | "offer_selected"
      | "checkout_started"
      | "campaign_viewed"
      | "withdrawal_requested",
    metadata: Record<string, string> = {}
  ) => {
    if (!event) {
      return;
    }

    callProviderFunc("trackPromotion", event, metadata);
  },
  trackBoostAd: () => {
    callProviderFunc("trackBoostAd");
  },
  trackPriceCheckShown: (
    productId?: string,
    confidence?: string,
    comparableSales?: number
  ) => {
    if (isNil(productId) || isNil(confidence) || isNil(comparableSales)) {
      return;
    }

    callProviderFunc(
      "trackPriceCheckShown",
      productId,
      confidence,
      comparableSales
    );
  },
  trackPriceCheckLowered: (
    productId?: string,
    suggestedPrice?: number,
    savedPrice?: number,
    edited?: boolean
  ) => {
    if (
      isNil(productId) ||
      isNil(suggestedPrice) ||
      isNil(savedPrice) ||
      isNil(edited)
    ) {
      return;
    }

    callProviderFunc(
      "trackPriceCheckLowered",
      productId,
      suggestedPrice,
      savedPrice,
      edited
    );
  },
  trackInviteFriend: () => {
    callProviderFunc("trackInviteFriend");
  },
  trackSignUp: () => {
    callProviderFunc("trackSignUp");
  },
  trackAdCreationStarted: () => {
    callProviderFunc("trackAdCreationStarted");
  },
  trackAdCreationSectionOpened: (name?: string, timestamp?: number) => {
    if (isNil(name) || isNil(timestamp)) {
      return;
    }

    callProviderFunc("trackAdCreationSectionOpened", name, timestamp);
  },
  trackAdCreationSectionComplete: (name?: string) => {
    if (isNil(name)) {
      return;
    }

    callProviderFunc("trackAdCreationSectionComplete", name);
  },
  trackAdCreationPhotoSelected: () => {
    callProviderFunc("trackAdCreationPhotoSelected");
  },
  trackAdCreationSubmitted: (aiAssistUsed?: boolean) => {
    callProviderFunc("trackAdCreationSubmitted", aiAssistUsed ?? false);
  },
  trackAdCreationCompleted: () => {
    callProviderFunc("trackAdCreationCompleted");
  },
  trackAdCreationAIUsed: () => {
    callProviderFunc("trackAdCreationAIUsed");
  },
  trackAdCreationClickedPlaceAd: () => {
    callProviderFunc("trackAdCreationClickedPlaceAd");
  },
  trackAdCreationStep: (step?: number) => {
    if (isNil(step)) {
      return;
    }

    callProviderFunc("trackAdCreationStep", step);
  },
  trackAdCreationDraftSaved: (product?: AnalyticsProduct) => {
    if (!product) {
      return;
    }

    callProviderFunc("trackAdCreationDraftSaved", product);
  },
  trackAdCreationV2: (event, payload) => {
    if (!event || !payload) {
      return;
    }

    callProviderFunc("trackAdCreationV2", event, payload);
  },
  trackPageView: (
    pageUrl?: string,
    pageTitle?: string,
    language?: string,
    me?: AnalyticsMe,
    logInStatus?: AnalyticsLogInStatus
  ) => {
    if (isNil(pageUrl) || isNil(pageTitle) || isNil(language)) {
      return;
    }

    callProviderFunc(
      "trackPageView",
      pageUrl,
      pageTitle,
      language,
      me,
      logInStatus
    );
  },
  trackLoginStart: () => {
    callProviderFunc("trackLoginStart");
  },
  trackSignUpStart: () => {
    callProviderFunc("trackSignUpStart");
  },
  trackSignUpProfileCompleted: () => {
    callProviderFunc("trackSignUpProfileCompleted");
  },
  trackAddToCartChat: () => {
    callProviderFunc("trackAddToCartChat");
  },
  trackPlaceBidChat: () => {
    callProviderFunc("trackPlaceBidChat");
  },
  trackPayNowChat: () => {
    callProviderFunc("trackPayNowChat");
  },
  trackMessageSentChat: () => {
    callProviderFunc("trackMessageSentChat");
  },
  identify: (me?: AnalyticsMe) => {
    if (isNil(me)) {
      return;
    }

    callProviderFunc("identify", me);
  },
  trackExperimentExposure: (
    experimentName?: string,
    experimentGroupName?: string
  ) => {
    if (isNil(experimentName) || isNil(experimentGroupName)) {
      return;
    }

    callProviderFunc(
      "trackExperimentExposure",
      experimentName,
      experimentGroupName
    );
  },
  trackAnnouncementBar: () => {
    callProviderFunc("trackAnnouncementBar");
  },
  trackSignUpBlock: (feature?: string) => {
    if (isNil(feature)) {
      return;
    }

    callProviderFunc("trackSignUpBlock", feature);
  },
  trackCalculateShipping: () => {
    callProviderFunc("trackCalculateShipping");
  },
  trackCalculateShippingSuccess: () => {
    callProviderFunc("trackCalculateShippingSuccess");
  },
  trackCreateAdClickAdsTable: () => {
    callProviderFunc("trackCreateAdClickAdsTable");
  },
  trackCreateAdClickNoAds: () => {
    callProviderFunc("trackCreateAdClickNoAds");
  },
  trackBulkUploadEntryClick: (entryPoint?: BulkUploadEntryPoint) => {
    if (isNil(entryPoint)) {
      return;
    }

    callProviderFunc("trackBulkUploadEntryClick", entryPoint);
  },
  trackClickedAppInstall: (device?: Device, eventSource?: string) => {
    if (isNil(device)) {
      return;
    }

    callProviderFunc("trackClickedAppInstall", device, eventSource);
  },
  trackChatSellNow: (value?: ChatMemberRole) => {
    if (isNil(value)) {
      return;
    }

    callProviderFunc("trackChatSellNow", value);
  },
  trackClickAppInstallBanner: () => {
    callProviderFunc("trackClickAppInstallBanner");
  },
  trackShowedAppInstallBanner: () => {
    callProviderFunc("trackShowedAppInstallBanner");
  },
  trackClosedAppInstallBanner: () => {
    callProviderFunc("trackClosedAppInstallBanner");
  },
  trackAdCreationOnBoarding: step => {
    if (isNil(step)) {
      return;
    }

    callProviderFunc("trackAdCreationOnBoarding", step);
  },
  trackAdCreationOnBoardingClose: reason => {
    if (isNil(reason)) {
      return;
    }

    callProviderFunc("trackAdCreationOnBoardingClose", reason);
  },
};
